import $ from 'jquery';
import Cookie from 'js-cookie';

let staticInstance = null;

class ConsentEvent {
    constructor(data) {
        $.extend(this, data || {});
    }
}


class ConsentApi {
    constructor(opts) {
        /**
         * @type {ConsentApiOptions}
         * @public
         */
        this.opts = $.extend({
            cookieName: 'dsgvo-settings',
            cookiePath: '/',
            snippetUrl: null,
            popupUrl: null,
        }, opts || {});

        /** @type {ScheduledEvents} */
        this._scheduledEvents = {};
    }

    static getInstance(...args) {
        if (args.length === 0) {
            args = [window.consentApiOptions];
        }
        if (staticInstance === null) {
            staticInstance = new ConsentApi(...args);
        }
        return staticInstance;
    }

    /**
     * @param {String} eventString - "enable:youtube" or "disable:google-maps"
     * @param {CategoryChangeCallback} callback
     * @param {*} [payload]
     */
    on(eventString, callback, payload) {
        eventString = eventString.split(':').map(s => s.trim()).join(':');
        this._scheduleEvent(eventString, {callback, payload});
    }

    /**
     * @param {String} eventString - one of "enable" or "disable"
     * @param {ScheduledEvent} event
     * @private
     */
    _scheduleEvent(eventString, event) {
        if (!this._scheduledEvents.hasOwnProperty(eventString)) {
            this._scheduledEvents[eventString] = [];
        }
        this._scheduledEvents[eventString].push(event);

        let {eventPart, identifierPart} = this._splitEvent(eventString);
        if (eventPart === 'enable' && this.isEnabled(identifierPart)) {
            event.callback(this._makeEvent(eventString), event.payload);
        } else if (eventPart === 'disable' && this.isDisabled(identifierPart)) {
            event.callback(this._makeEvent(eventString), event.payload);
        }
    }

    _splitEvent(eventString) {
        let parts = eventString.split(':');
        let eventPart = parts[0];
        let identifierPart = parts.slice(1).join(':');
        return {
            eventPart: eventPart,
            identifierPart: (identifierPart ? identifierPart : null),
        };
    }

    /**
     * @param {String} eventString
     * @param {*} [extra]
     * @private
     */
    _triggerChange(eventString, extra) {
        if (!this._scheduledEvents.hasOwnProperty(eventString)) {
            return;
        }
        let events = this._scheduledEvents[eventString];
        if (!Array.isArray(events)) {
            return;
        }
        const ev = this._makeEvent(eventString, extra);
        for (let i = 0; i < events.length; i++) {
            events[i].callback(ev, events[i].payload);
        }
    }

    /**
     * @param {String} eventString
     * @param {*} [extra]
     * @return {ConsentEvent}
     * @private
     */
    _makeEvent(eventString, extra) {
        let {eventPart, identifierPart} = this._splitEvent(eventString);
        if (identifierPart) {
            return new ConsentEvent({event: eventPart, identifier: identifierPart});
        } else {
            let eventData = $.extend({}, extra || {}, {event: eventPart});
            return new ConsentEvent(eventData);
        }
    }

    /**
     * @return {CookieSetup}
     */
    settings() {
        let settings = null;
        try {
            let cookieData = Cookie.get(this.opts.cookieName);
            if (cookieData) {
                settings = JSON.parse(cookieData);
            }
        } catch (e) {
            console.warn(e);
        }

        if (!settings || !settings.confirmed) {
            settings = {confirmed: false, identifiers: []};
        }

        if (!Array.isArray(settings.identifiers)) {
            settings.identifiers = [];
        }

        return settings;
    }

    /**
     * @return {String[]}
     */
    identifiers() {
        let settings = this.settings();
        return settings.identifiers.slice();
    }

    /**
     * @param identifier {String}
     * @param force {Boolean} - mostly used by inline element, to enforce confirmation
     */
    enable(identifier, force = false) {
        let settings = this.settings();
        settings.confirmed = settings.confirmed || force;

        if (settings.confirmed && settings.identifiers.indexOf(identifier) === -1) {
            settings.identifiers.push(identifier);
            this._save(settings);
            //this.loadSnippets();
            this._triggerChange(`enable:${identifier}`);
        }
    }

    /**
     * @param identifier {String}
     * @param force {Boolean} - mostly used by inline element, to enforce confirmation
     */
    disable(identifier, force=false) {
        let settings = this.settings();
        settings.confirmed = settings.confirmed || force;

        if (settings.confirmed && settings.identifiers.indexOf(identifier) > -1) {
            settings.identifiers = settings.identifiers.filter(e => e !== identifier);
            this._save(settings);
            this._triggerChange(`disable:${identifier}`);
        }
    }

    /**
     * @param identifier {String}
     * @return {boolean}
     */
    isEnabled(identifier) {
        return this.isConfirmed() && this.identifiers().indexOf(identifier) !== -1;
    }

    /**
     * @param identifier {String}
     * @return {boolean}
     */
    isDisabled(identifier) {
        return !this.isEnabled(identifier);
    }

    /**
     * @param identifiers {String[]}
     */
    confirm(identifiers) {
        this._triggerChange('confirming', {identifiers});
        let settings = this.settings();
        let oldIdentifiers = settings.identifiers.slice();
        settings.confirmed = true;
        settings.identifiers = identifiers.slice();
        this._save(settings);
        this.loadSnippets();

        let addedIdentifiers = this._diff(identifiers, oldIdentifiers);
        let deletedIdentifiers = this._diff(oldIdentifiers, identifiers);

        for (let j = 0; j < addedIdentifiers.length; j++) {
            this._triggerChange(`enable:${addedIdentifiers[j]}`);
        }
        for (let j = 0; j < deletedIdentifiers.length; j++) {
            this._triggerChange(`disable:${deletedIdentifiers[j]}`);
        }
        this._triggerChange('confirmed', {identifiers});
    }

    /**
     * @param {Array.<T>} lstA
     * @param {Array.<T>} lstB
     * @return {Array.<T>}
     * @private
     * @template T
     */
    _diff(lstA, lstB) {
        let newA = [];
        for (let i = 0; i < lstA.length; i++) {
            if (lstB.indexOf(lstA[i]) === -1) {
                newA.push(lstA[i]);
            }
        }
        return newA;
    }

    /**
     * @return {Boolean}
     */
    isConfirmed() {
        let settings = this.settings();
        return !!(settings && settings.confirmed);
    }

    loadPopup() {
        return new Promise((resolve, reject) => {
            fetch(this.opts.popupUrl).then(e => e.json()).then(/** @param data {ConsentPopupResponse} */data => {
                resolve($(data.popup));
            }).catch(reject);
        });
    }

    loadSnippets() {
        fetch(this.opts.snippetUrl).then(e => e.json()).then(/** @param data {ConsentSnippetResponse} */data => {
            data.snippets.head.forEach(snippet => {
                this._attachSnippet(snippet, document.head, 'end');
            });
            data.snippets.body_start.forEach(snippet => {
                this._attachSnippet(snippet, document.body, 'start');
            });
            data.snippets.body_end.forEach(snippet => {
                this._attachSnippet(snippet, document.body, 'end');
            });
        });
    }

    _attachSnippet(snippet, parent, rel) {
        this._attachedSnippets = this._attachedSnippets || [];
        if (this._attachedSnippets.indexOf(snippet.uid) !== -1) {
            // snippet already loaded and attached.
            return;
        }

        if (rel === 'end') {
            this._attachedSnippets.push(snippet.uid);
            $(parent).append(snippet.markup);
        } else if (rel === 'start') {
            this._attachedSnippets.push(snippet.uid);
            $(parent).prepend(snippet.markup);
        }
    }

    /**
     * @param settings {CookieSetup}
     * @private
     */
    _save(settings) {
        let date;
        if (settings.expires) {
            date = new Date(settings.expires);
        } else {
            date = new Date(Date.now() + (864e5 * 30));
        }
        let settingsJson = JSON.stringify(settings)
        Cookie.set(this.opts.cookieName, settingsJson, {
            expires: date,
            path: this.opts.cookiePath,
        });
    }
}

export default ConsentApi;
